import React, {Component, ReactNode, SyntheticEvent} from 'react';
import {Checkbox} from '@homeaway/react-form-components';
import {findDOMNode} from 'react-dom';
import isEqual from 'lodash/isEqual';
import {MAX_ELEMENTS_VISIBLE_COLLAPSED} from '../../constants/filters';
import Tooltip from '@homeaway/react-tooltip';
import {SVGIcon} from '@homeaway/react-svg';
import {INFO__16} from '@homeaway/svg-defs';

type Filter = {
    description?: string;
    refineByQueryArgument: string;
    id: string;
    groupId?: string;
    name: string;
};

/* istanbul ignore next */
export type Refinement = {
    count?: number;
    checked: boolean;
    filter: Filter;
};

export type RefinementTooltip = {
    isMobile: boolean;
};

type Props = {
    checked: string[];
    expanded?: boolean;
    filters: Refinement[];
    groupName: string;
    filterGroupId: string;
    secondaryLabel?: ReactNode;
    handleChecked: (event: SyntheticEvent<HTMLInputElement>) => void;
    tooltipOptions: RefinementTooltip;
};

const isFilterChecked = (checked: string[], filterId: string): boolean => {
    return checked.includes(filterId);
};

const checkedInGroup = (checked: string[], filters: Refinement[]): string[] => {
    const idsInGroup = filters.reduce(
        (prev: Record<string, any>, cur: Refinement) => {
            prev[cur.filter.refineByQueryArgument] = true;
            return prev;
        },
        {}
    );

    const filtered = checked.filter((c) => idsInGroup[c]);
    filtered.sort();
    return filtered;
};

const canonicalizeFilters = (filters: Refinement[]) =>
    filters.map((f) => ({...f, count: f.count && f.count > 0 ? 1 : 0}));

export class FilterGroup extends Component<Props> {
    /**
     * We only update if the checked filters in the group changes or if any of the counts of the filters changes from non-zero to zero and vice versa
     * @param {Props} nextProps
     */
    shouldComponentUpdate(nextProps: Props) {
        const nextFilters = nextProps.filters;
        const nextChecked = nextProps.checked;
        const nextExpanded = nextProps.expanded;
        const nextTooltipOptions = nextProps.tooltipOptions;
        const {filters, checked, expanded, tooltipOptions} = this.props;

        return (
            !isEqual(
                canonicalizeFilters(nextFilters),
                canonicalizeFilters(filters)
            ) ||
            !isEqual(
                checkedInGroup(checked, filters),
                checkedInGroup(nextChecked, filters)
            ) ||
            nextExpanded !== expanded ||
            nextTooltipOptions !== tooltipOptions
        );
    }

    /* eslint-disable complexity */
    componentDidUpdate(prevProps: Props) {
        // Place focus on the last visible element in the FilterGroup in collapsed state, so that
        // after expanding it, we could stay at the same position.
        if (this.props.expanded && !prevProps.expanded) {
            const node = findDOMNode(this);
            let filterCheckboxes;
            if (node) {
                filterCheckboxes = node.querySelectorAll(
                    'input[type="checkbox"]'
                );
            }
            if (
                filterCheckboxes &&
                filterCheckboxes.length >= MAX_ELEMENTS_VISIBLE_COLLAPSED
            ) {
                filterCheckboxes[MAX_ELEMENTS_VISIBLE_COLLAPSED].focus();
            }
        }
    }
    /* eslint-enable complexity */

    render() {
        const {
            props: {
                checked,
                expanded,
                filters,
                groupName,
                filterGroupId,
                secondaryLabel,
                handleChecked,
                tooltipOptions: {isMobile},
            },
        } = this;

        const ariaGroupId = groupName.split(' ').join('_');
        return (
            <div
                role="group"
                aria-describedby={ariaGroupId}
                className="FilterGroup"
            >
                <h2 id={ariaGroupId} className="FilterGroup__label">
                    {groupName}
                </h2>
                {secondaryLabel && (
                    <div className="FilterGroup__secondaryLabel">
                        {secondaryLabel}
                    </div>
                )}
                {/* eslint-disable complexity */}
                {filters.map((searchFilter, index) => {
                    const {filter: filterDescription} = searchFilter;
                    const filterId = filterDescription.refineByQueryArgument;
                    const isChecked = isFilterChecked(checked, filterId);
                    const isDisabled =
                        !isChecked &&
                        searchFilter.count &&
                        searchFilter.count <= 2;
                    const currentTabIndex =
                        !expanded && index >= MAX_ELEMENTS_VISIBLE_COLLAPSED
                            ? '-1'
                            : '0';
                    return (
                        <div className="FilterGroup--refinement" key={filterId}>
                            <Checkbox
                                data-wdio={`checkbox__${filterId}`}
                                data-groupid={filterGroupId}
                                name={filterId}
                                checked={isChecked}
                                label={filterDescription.name}
                                onChange={handleChecked}
                                disabled={isDisabled}
                                tabIndex={currentTabIndex}
                                aria-label={`${groupName} ${filterDescription.name}`}
                            />
                            {filterDescription.description && (
                                <Tooltip
                                    content={filterDescription.description}
                                    delayShow={isMobile ? 0 : 400}
                                    fadeOut={false}
                                    placement="bottom"
                                    trigger={isMobile ? 'click' : 'hover focus'}
                                    wrapperClassName="FilterGroup--refinement__tooltip"
                                >
                                    <SVGIcon
                                        markup={INFO__16}
                                        className="DisclaimerMessaging__icon"
                                        aria-hidden
                                    />
                                </Tooltip>
                            )}
                        </div>
                    );
                })}
                {/* eslint-enable complexity */}
            </div>
        );
    }
}

export default FilterGroup;
